import { FormOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMemoizedFn } from 'ahooks';
import { Button, Drawer, Input, message, Popconfirm, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { arrayMoveImmutable } from 'array-move';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import {
  deleteGroup,
  sortPhraseGroupV2,
  updateGroup,
  updateGroupEnable,
} from 'services/api/phrase';
import { DragHandle, SortableItem, DraggableContainer } from 'components/DragTableComponent';
import { NAME_MAX_LENGTH } from './GroupAdd';
import useModal from 'hooks/useModal';
import { StoreScopesModal, getStoreCount } from 'components/StoreScopes';
import {
  storeScopes2applyScope,
  useApplyScope2storeScopes,
} from 'views/Aide/Basis/Remind/TaskForm/share';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { usePhraseContext } from '../PhraseContext';
import StatusSwitch from 'components/StatusSwitch';

const EditCell = styled.div`
  padding: 5px 12px;
  cursor: pointer;

  &:hover {
    padding: 4px 11px;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
  }
`;

export interface GroupManageProps {
  data?: Phrase.Group[];
  visible: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
}

const EditName = (props: { data: Phrase.Group; onUpdate?: () => void }) => {
  const { data, onUpdate } = props;
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(data.name);

  const { t } = useTranslate();

  const toggleEdit = useMemoizedFn(() => {
    setEditing(!editing);
  });

  const save = useMemoizedFn(() => {
    updateGroup({
      phraseGroupId: data.id,
      name: inputValue,
      applyScope: data.applyScope,
    })
      .then(() => {
        onUpdate?.();
      })
      .catch(() => {
        message.error(t('api.common.error.messages', { action: t('api.common.action.save') }));
      });
    toggleEdit();
  });

  return editing ? (
    <Input
      autoFocus
      maxLength={NAME_MAX_LENGTH}
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
      }}
      onPressEnter={save}
      onBlur={save}
    />
  ) : (
    <EditCell onClick={toggleEdit}>{data.name}</EditCell>
  );
};

export const GroupManage = (props: GroupManageProps) => {
  const { visible, data = [], onClose, onUpdate, onDelete } = props;
  const { t } = useTranslate();

  const { platform, groupType } = usePhraseContext();

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      // 重新排序
      const sortNumber = data.map((d) => d.sortNumber);
      const sortedData = arrayMoveImmutable(data.slice(), oldIndex, newIndex);
      const sortMap: Record<string, string> = {};
      sortedData.forEach((d, i) => {
        sortMap[d.id] = sortNumber[i];
      });
      sortPhraseGroupV2({
        sortMap,
      })
        .then(() => {
          onUpdate?.();
        })
        .catch(() => {
          message.error(t('api.common.error.messages', { action: t('api.common.action.request') }));
        });
    }
  };

  const Container = (props: SortableContainerProps) => (
    <DraggableContainer onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = data?.findIndex((x) => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const { applyScope2storeScopes } = useApplyScope2storeScopes({
    platform,
    permId: permissionNameIdMap['快捷回复-团队模版'],
  });

  const [scopeLoadingId, setScopeLoadingId] = useState<Phrase.Group['id']>();

  const scopesModal = useModal<Phrase.Group>();

  const { meta } = scopesModal;

  const { applyScope } = meta || {};

  const scopes =
    !applyScope || applyScope.scope === 'ALL' ? [] : applyScope2storeScopes(applyScope);

  const modal = meta && (
    <StoreScopesModal
      supportWa={false}
      showFooterTips={false}
      platform={platform}
      permId={permissionNameIdMap['快捷回复-团队模版']}
      visible={scopesModal.visible}
      value={scopes}
      onCancel={() => scopesModal.hide()}
      onChange={(scopes) => {
        setScopeLoadingId(meta.id);

        updateGroup({
          phraseGroupId: meta.id,
          name: meta.name,
          applyScope: storeScopes2applyScope(scopes),
        })
          .then(() => {
            onUpdate?.();

            message.success(t('operation.saveSucceed'));
          })
          .catch(() => {
            message.error(t('phrase.保存失败'));
          })
          .finally(() => {
            setScopeLoadingId(undefined);
          });
      }}
    />
  );

  const columns: ColumnsType<Phrase.Group> = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'sort',
        width: 30,
        className: 'drag-visible',
        render: () => <DragHandle />,
      },
      {
        title: t('phrase.分组名称'),
        dataIndex: 'name',
        className: 'drag-visible',
        render: (_, record) => {
          return <EditName data={record} onUpdate={onUpdate} />;
        },
      },
      {
        title: t('phrase.快捷回复数量'),
        dataIndex: 'phraseRootNum',
        width: 180,
      },
      ...(groupType === 'TEAM'
        ? [
            {
              title: t('aide.应用店铺'),
              dataIndex: 'applyScope',
              width: 200,
              render: (applyScope: Phrase.Group['applyScope'], record: Phrase.Group) => {
                const scopes =
                  !applyScope || applyScope.scope === 'ALL'
                    ? []
                    : applyScope2storeScopes(applyScope);

                return (
                  <div>
                    {applyScope && applyScope.scope === 'ALL'
                      ? t('department.全部店铺')
                      : t('department.已选x店铺', { x: getStoreCount(scopes) })}
                    <Button
                      type="link"
                      loading={scopeLoadingId === record.id}
                      icon={<FormOutlined />}
                      onClick={() => scopesModal.show(record)}
                    >
                      {t('marketing.编辑')}
                    </Button>
                  </div>
                );
              },
            },
          ]
        : []),
      {
        title: (
          <Space size={4}>
            {t('review.状态')}
            <Tooltip title={t('phrase.分组状态提示')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </Space>
        ),
        dataIndex: 'enable',
        render: (enable, { id }) => {
          return (
            <StatusSwitch
              checkedChildren={t('aide.开')}
              unCheckedChildren={t('aide.关')}
              checked={enable}
              onChange={(enable) =>
                updateGroupEnable({
                  phraseGroupId: id,
                  enable,
                })
                  .then(() => {
                    onUpdate?.();
                  })
                  .catch(() => {
                    message.error(
                      t('api.common.error.messages', { action: t('api.common.action.request') }),
                    );
                  })
              }
            />
          );
        },
      },
      {
        title: t('操作'),
        render: (_, record) => {
          return (
            <Popconfirm
              title={<p>{t('phrase.确认删除该分组？组内快捷回复将一并删除')}</p>}
              placement="left"
              onConfirm={() => {
                deleteGroup({
                  phraseGroupId: record.id,
                })
                  .then(() => {
                    onDelete?.();
                    message.success(t('phrase.删除成功'));
                  })
                  .catch(() => {
                    message.success(t('phrase.删除失败，请稍后再试'));
                  });
              }}
            >
              <Button type="link">{t('phrase.删除')}</Button>
            </Popconfirm>
          );
        },
      },
    ],
    [onDelete, onUpdate, t, groupType, applyScope2storeScopes, scopesModal, scopeLoadingId],
  );

  return (
    <Drawer
      title={t('phrase.管理分组')}
      getContainer={false}
      visible={visible}
      onClose={onClose}
      width={groupType === 'TEAM' ? 840 : 640}
      style={{ position: 'absolute' }}
    >
      {modal}
      <Table
        pagination={false}
        dataSource={data}
        columns={columns}
        rowKey="id"
        components={{
          body: {
            wrapper: Container,
            row: DraggableBodyRow,
          },
        }}
      />
    </Drawer>
  );
};
